import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, map, of, switchMap, tap } from 'rxjs';

import { AppConfigService } from 'app/core/config/app-config.service';
import { AuthHttpService } from 'app/core/auth/auth-http-service';
import { CompaniesService } from '@app/services/companies.service';
import { PresenceService } from '@app/services/presence.service';
import { VirtualClassroomService } from '@app/services/vclassroom.service';

/** Browser storage that makes up a session: the auth store, the encrypted settings, the header company. */
const LOCAL_KEYS = ['auth', 'st_sys_cfg', 'st_cmp_cfg'];
const SESSION_KEYS = ['selectedCompanyId'];
export const IMPERSONATION_STASH_KEY = 'auth_impersonator';

interface ImpersonationStash {
  local: Record<string, string | null>;
  session: Record<string, string | null>;
  returnUrl: string;
  name: string;
  roleLabel: string;
}

/**
 * "Login as user". The admin's own session is set aside in localStorage while the
 * impersonated session runs; "Return to my account" writes it back and reloads.
 */
@Injectable({ providedIn: 'root' })
export class ImpersonationService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly appConfig = inject(AppConfigService);
  private readonly authHttpService = inject(AuthHttpService);
  private readonly companiesService = inject(CompaniesService);
  private readonly presenceService = inject(PresenceService);
  private readonly vclassService = inject(VirtualClassroomService);

  /** Read once: both transitions end in a full page load. */
  private readonly stash: ImpersonationStash | null = readStash();

  get isActive(): boolean {
    return this.stash !== null;
  }

  get impersonatedName(): string {
    return this.stash?.name ?? '';
  }

  get impersonatedRole(): string {
    return this.stash?.roleLabel ?? '';
  }

  /** Opens a session as the account, keeping the current one to return to. */
  start(accountId: number, name: string, roleLabel: string): Observable<void> {
    const serverUrl = this.appConfig.get<string>('serverUrl', '');
    return this.http.post<any>(`${serverUrl}/v1/impersonate/${accountId}`, {}).pipe(
      tap((res) => {
        if (res?.status !== 'OK' || !res?.data?.access_token) {
          throw new Error(res?.message || 'Login as user failed');
        }
        const stash: ImpersonationStash = {
          local: snapshot(localStorage, LOCAL_KEYS),
          session: snapshot(sessionStorage, SESSION_KEYS),
          returnUrl: this.router.url,
          name,
          roleLabel,
        };
        this.presenceService.stopPresence(true);
        this.vclassService.disconnectRoom();
        this.companiesService.clearSelection();
        this.authHttpService.applyLoginResponse(res);
        localStorage.setItem(IMPERSONATION_STASH_KEY, JSON.stringify(stash));
      }),
      switchMap(() => this.router.navigateByUrl('/home')),
      map(() => this.reload()),
    );
  }

  /** Ends the impersonated session and brings the admin back where they started. */
  stop(): Observable<void> {
    const stash = this.stash;
    if (!stash) {
      return of(undefined);
    }
    const serverUrl = this.appConfig.get<string>('serverUrl', '');
    return this.http.post<any>(`${serverUrl}/v5/logout`, {}).pipe(
      catchError(() => of(null)),
      map(() => {
        this.presenceService.stopPresence(true);
        this.vclassService.disconnectRoom();
        restore(localStorage, stash.local);
        restore(sessionStorage, stash.session);
        localStorage.removeItem(IMPERSONATION_STASH_KEY);
        this.navigateTo(stash.returnUrl || '/home');
      }),
    );
  }

  /** Full page loads, so every service and guard starts from the new session. */
  protected reload(): void {
    window.location.reload();
  }

  protected navigateTo(url: string): void {
    window.location.assign(url);
  }
}

function readStash(): ImpersonationStash | null {
  try {
    const raw = localStorage.getItem(IMPERSONATION_STASH_KEY);
    return raw ? (JSON.parse(raw) as ImpersonationStash) : null;
  } catch {
    return null;
  }
}

function snapshot(storage: Storage, keys: string[]): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  for (const key of keys) {
    out[key] = storage.getItem(key);
  }
  return out;
}

function restore(storage: Storage, values: Record<string, string | null>): void {
  for (const [key, value] of Object.entries(values)) {
    if (value === null) {
      storage.removeItem(key);
    } else {
      storage.setItem(key, value);
    }
  }
}
